import { useTranslation } from 'react-i18next';

import { useGetWalletsQuery } from '@/services/qbankApi';
import { QBANK_DEFAULT_COIN, type Wallet } from '@/types/qbank';
import { Loader } from '@/components/common/Loader';

/**
 * Wallet & Levels page — reached from the Profilo "Settings" block. Lists every
 * coin balance + EXP level. QBank creates wallet rows lazily, so an entitled but
 * uncredited user gets a "0" default-coin row. Non-entitled users (no
 * `qbank:wallet:read` scope → 401/error) see an empty state.
 */
export function WalletLevels() {
  const { t } = useTranslation('general');
  /**
   * 🔴 SI RILEGGE A OGNI RIENTRO NELLA PAGINA. Ordine di Luca (2026-08-30, subito dopo la prima
   * riscossione vera): *«L'app pagina wallet doveva aggiornarsi, fai in modo che si riaggiorni
   * ogni volta che si rientra nella pagina wallet»*.
   *
   * `settings/wallet` e' un elemento di rotta sotto l'`<Outlet />` di AppShell: uscire lo SMONTA e
   * rientrare lo rimonta, quindi «rientro nella pagina» e' letteralmente il montaggio di questo
   * componente — verificato in `router.tsx`, non assunto. Senza questa riga, `keepUnusedDataFor:
   * 120` restituiva la copia in cache per un massimo di due minuti e si rientrava sul saldo
   * vecchio.
   *
   * ⚠️ OPZIONE LOCALE, NON CONFIGURAZIONE DELLO SLICE. `useGetWalletsQuery` ha quattro consumatori
   * (`CurrencyHud`, `HubSections`, `RankedPanel`, e questa pagina): mettere `refetchOnFocus: true`
   * su `qbankApi` avrebbe cambiato il comportamento di tutti e quattro e sarebbe scattato a ogni
   * ritorno sull'app invece che a ogni rientro qui — piu' largo di quanto chiesto, su superficie
   * condivisa che non appartiene alla sezione Missioni. L'ordine riguarda LA PAGINA, e la risposta
   * sta nella pagina. Stesso idioma gia' in uso nel repo (`ChessHubSection.tsx:25`,
   * `ChessChallenges.tsx:46`, `Tournaments.tsx`): nessuna convenzione nuova.
   *
   * E' complementare, non alternativa, all'invalidazione del tag `Wallet` che il claim dispaccia
   * da `questApi`: quella copre «riscuoto e resto dove sono», questa copre «riscuoto, esco,
   * torno». Le due esperienze sono diverse e nessuna delle due copre l'altra.
   */
  const qbankWallets = useGetWalletsQuery(undefined, { refetchOnMountOrArgChange: true });
  const wallets = qbankWallets.data ?? [];
  const displayWallets: Wallet[] = wallets.some((w) => w.level == null)
    ? wallets
    : [
        ...wallets,
        {
          currencyId: '__default__',
          sub: '',
          name: QBANK_DEFAULT_COIN.name,
          code: QBANK_DEFAULT_COIN.code,
          exchange: null,
          isDefault: true,
          amount: 0,
          defaultCoin: null,
          isEnable: true,
          level: null,
          toNextLevel: null,
        },
      ];

  return (
    <div className="flex flex-col gap-6 px-4 pb-6 pt-5">
      <header>
        <h1 className="text-2xl font-bold text-fg">
          {t('qbank.wallet_levels', { defaultValue: 'Wallet & Levels' })}
        </h1>
      </header>

      {qbankWallets.isLoading ? (
        <div className="flex h-24 items-center justify-center">
          <Loader />
        </div>
      ) : qbankWallets.isError ? (
        <div className="card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          {t('qbank.no_wallet', { defaultValue: 'No wallet on this account yet.' })}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {displayWallets.map((w) => (
            <li
              key={w.currencyId}
              className="card-edge flex items-center justify-between rounded-xl bg-bg-secondary p-3"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-fg">{w.name}</p>
                <p className="text-xs text-fg-muted">{w.code}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-brand">
                  {Number(w.amount ?? 0).toLocaleString()}
                </p>
                {w.level != null && (
                  <p className="text-[11px] text-fg-muted">
                    {t('qbank.level', { defaultValue: 'Lv {{n}}', n: w.level })}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
